import { Eye, Trash2 } from 'lucide-react';
import type { SharedLinkItem } from '../types/reports';
import { fmtReportDate, getThemeSwatch } from '../utils/reportsUtils';

type SharedLinkRowProps = {
  item: SharedLinkItem;
  index: number;
  onView: () => void;
  onDelete: () => void;
};

export function SharedLinkRow({ item, index, onView, onDelete }: SharedLinkRowProps) {
  const isAllowComments = item.permission === 'ALLOW_COMMENTS';

  return (
    <div className="group flex flex-col gap-4 p-5 transition hover:bg-[var(--dash-soft)]/40 md:flex-row md:items-center md:justify-between">
      <div className="min-w-0 flex-1 pr-4 text-left">
        <div className="mb-1.5 flex flex-wrap items-center gap-3">
          <span className="font-bold text-[var(--dash-ink)]">{index + 1}.</span>
          <span className="truncate text-sm font-bold text-[var(--dash-ink)]">
            ID: {item.shareId || 'N/A'}
          </span>
          <span
            className={`rounded border px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase ${
              isAllowComments
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-[var(--dash-line)] bg-[var(--dash-soft)] text-[var(--dash-muted)]'
            }`}
          >
            {(item.permission || 'READ_ONLY').replace('_', ' ')}
          </span>
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full shadow-inner"
            style={{ backgroundColor: getThemeSwatch(item.themeColor) }}
            title={`Theme: ${item.themeColor || 'Default'}`}
          />
        </div>

        {item.note ? (
          <p className="truncate text-sm text-[var(--dash-muted)]" title={item.note}>
            Note: {item.note}
          </p>
        ) : (
          <p className="text-sm text-[var(--dash-muted)] italic">No note attached</p>
        )}

        {item.shareableLink ? (
          <p className="truncate text-sm text-[var(--dash-muted)]">
            <span className="font-medium text-[var(--dash-ink)]">Shareable Link</span>:{' '}
            {item.shareableLink}
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-6">
        <div className="hidden text-right sm:block">
          <p className="m-0 text-[10px] font-bold text-[var(--dash-muted)] uppercase">
            Expires On
          </p>
          <p className="m-0 text-sm font-medium text-[var(--dash-ink)]">
            {fmtReportDate(item.expiresAt)}
          </p>
        </div>

        <div className="hidden h-8 w-px bg-[var(--dash-line)] md:block" />

        <button
          type="button"
          onClick={onView}
          title="View Dashboard"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--dash-line)] bg-white text-[var(--dash-muted)] shadow-sm transition hover:border-[var(--dash-brand)] hover:bg-[var(--dash-brand)] hover:text-white"
        >
          <Eye className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          title="Delete"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--dash-line)] bg-white text-[var(--dash-muted)] shadow-sm transition hover:border-red-400 hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
